import type { LngLat } from "./types.ts";

/** MapLibre tiles are 512 px, so the world is 512 · 2^zoom pixels wide. */
const TILE_PX = 512;
const MAX_LAT = 85.051129;

export interface RotatedFit {
  center: LngLat;
  zoom: number;
  bearing: number;
}

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface FitOptions {
  width: number;
  height: number;
  /** Screen px kept clear on every side, or per side (for panels over the map). */
  padding: number | Insets;
  maxZoom: number;
  minZoom?: number;
}

export interface ScreenRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Map space each panel hides, in px from each edge of a `width`×`height` map. A panel
 * takes the side that costs the least room (a side panel takes left or right, a bottom
 * sheet takes the bottom). A side that would hide more than `maxShare` of the map is
 * left alone, so a panel covering most of a small screen doesn't squeeze the fit away.
 */
export function panelInsets(width: number, height: number, panels: readonly ScreenRect[], maxShare = 0.75): Insets {
  const out: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
  for (const p of panels) {
    if (p.right <= 0 || p.bottom <= 0 || p.left >= width || p.top >= height) continue;
    const sides: Array<[keyof Insets, number, number]> = [
      ["left", p.right, width],
      ["right", width - p.left, width],
      ["top", p.bottom, height],
      ["bottom", height - p.top, height],
    ];
    sides.sort((a, b) => a[1] / a[2] - b[1] / b[2]);
    const [side, px, span] = sides[0]!;
    if (px > span * maxShare) continue;
    out[side] = Math.max(out[side], px);
  }
  return out;
}

function toInsets(padding: number | Insets): Insets {
  return typeof padding === "number" ? { top: padding, right: padding, bottom: padding, left: padding } : padding;
}

function toMercator([lng, lat]: LngLat): [number, number] {
  const φ = (Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * Math.PI) / 180;
  return [(lng + 180) / 360, (1 - Math.log(Math.tan(Math.PI / 4 + φ / 2)) / Math.PI) / 2];
}

function fromMercator([x, y]: [number, number]): LngLat {
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
  return [x * 360 - 180, lat];
}

/**
 * Center and zoom that fit `points` in a `width`×`height` view turned to `bearing`
 * (MapLibre degrees, clockwise from north). Fits the points in the turned frame, so a
 * route drawn on a rotated canvas fills the view instead of its north-up bounding box.
 */
export function fitRotated(points: readonly LngLat[], bearing: number, opts: FitOptions): RotatedFit | null {
  if (!points.length) return null;
  const θ = (bearing * Math.PI) / 180;
  const cos = Math.cos(θ);
  const sin = Math.sin(θ);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    const [x, y] = toMercator(p);
    const sx = x * cos + y * sin;
    const sy = -x * sin + y * cos;
    minX = Math.min(minX, sx);
    maxX = Math.max(maxX, sx);
    minY = Math.min(minY, sy);
    maxY = Math.max(maxY, sy);
  }
  const pad = toInsets(opts.padding);
  const availW = Math.max(1, opts.width - pad.left - pad.right);
  const availH = Math.max(1, opts.height - pad.top - pad.bottom);
  const w = maxX - minX;
  const h = maxY - minY;
  const scale = Math.min(w > 0 ? availW / w : Infinity, h > 0 ? availH / h : Infinity);
  let zoom = Number.isFinite(scale) ? Math.log2(scale / TILE_PX) : opts.maxZoom;
  zoom = Math.min(opts.maxZoom, Math.max(opts.minZoom ?? 0, zoom));
  // The route centers in the clear area; the map center sits off it by the padding imbalance.
  const worldPx = TILE_PX * 2 ** zoom;
  const cx = (minX + maxX) / 2 - (pad.left - pad.right) / 2 / worldPx;
  const cy = (minY + maxY) / 2 - (pad.top - pad.bottom) / 2 / worldPx;
  const center = fromMercator([cx * cos - cy * sin, cx * sin + cy * cos]);
  return { center, zoom, bearing };
}

/**
 * Edge insets for overlays that actually cover that edge. A top-right card does not
 * become a full-height column or a full-width top bar; those stay in `corners`.
 */
export function splitOverlayInsets(
  width: number,
  height: number,
  panels: readonly ScreenRect[],
  minSpan = 0.5,
): { insets: Insets; corners: ScreenRect[] } {
  const insets: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
  const corners: ScreenRect[] = [];
  for (const p of panels) {
    if (p.right <= 0 || p.bottom <= 0 || p.left >= width || p.top >= height) continue;
    const hSpan = Math.max(0, Math.min(p.bottom, height) - Math.max(p.top, 0));
    const wSpan = Math.max(0, Math.min(p.right, width) - Math.max(p.left, 0));
    let edge = false;
    if (p.left <= 24 && hSpan >= height * minSpan) {
      insets.left = Math.max(insets.left, Math.min(width, p.right));
      edge = true;
    }
    if (width - p.right <= 24 && hSpan >= height * minSpan) {
      insets.right = Math.max(insets.right, Math.min(width, width - p.left));
      edge = true;
    }
    if (p.top <= 24 && wSpan >= width * minSpan) {
      insets.top = Math.max(insets.top, Math.min(height, p.bottom));
      edge = true;
    }
    if (height - p.bottom <= 24 && wSpan >= width * minSpan) {
      insets.bottom = Math.max(insets.bottom, Math.min(height, height - p.top));
      edge = true;
    }
    if (!edge) corners.push(p);
  }
  return { insets, corners };
}

function rotatedSpan(points: readonly LngLat[], bearing: number): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const θ = (bearing * Math.PI) / 180;
  const cos = Math.cos(θ);
  const sin = Math.sin(θ);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    const [x, y] = toMercator(p);
    const sx = x * cos + y * sin;
    const sy = -x * sin + y * cos;
    minX = Math.min(minX, sx);
    maxX = Math.max(maxX, sx);
    minY = Math.min(minY, sy);
    maxY = Math.max(maxY, sy);
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

/** Screen box of the fitted points, expanded by `chrome` (stroke, marker, label). */
export function fittedScreenBox(
  points: readonly LngLat[],
  bearing: number,
  view: RotatedFit,
  width: number,
  height: number,
  padding: Insets,
  chrome: Insets,
): ScreenRect | null {
  const span = rotatedSpan(points, bearing);
  if (!span) return null;
  const worldPx = TILE_PX * 2 ** view.zoom;
  const spanW = (span.maxX - span.minX) * worldPx;
  const spanH = (span.maxY - span.minY) * worldPx;
  const availW = Math.max(1, width - padding.left - padding.right);
  const availH = Math.max(1, height - padding.top - padding.bottom);
  const left = padding.left + (availW - spanW) / 2 - chrome.left;
  const top = padding.top + (availH - spanH) / 2 - chrome.top;
  return {
    left,
    top,
    right: left + spanW + chrome.left + chrome.right,
    bottom: top + spanH + chrome.top + chrome.bottom,
  };
}

/** How far a padding change moves the center of the padded rectangle, in screen px. */
function paddingCenterShift(before: Insets, after: Insets): number {
  const dx = ((after.left - before.left) - (after.right - before.right)) / 2;
  const dy = ((after.top - before.top) - (after.bottom - before.bottom)) / 2;
  return Math.hypot(dx, dy);
}

function hitsObstacle(visual: ScreenRect, obs: ScreenRect, gap: number): boolean {
  return visual.left < obs.right + gap && visual.right > obs.left - gap && visual.top < obs.bottom + gap && visual.bottom > obs.top - gap;
}

function pushPadding(padding: Insets, visual: ScreenRect, obs: ScreenRect, gap: number): Insets[] {
  const zoneL = obs.left - gap;
  const zoneT = obs.top - gap;
  const zoneR = obs.right + gap;
  const zoneB = obs.bottom + gap;
  const pushes: Array<[keyof Insets, number]> = [
    ["right", visual.right - zoneL],
    ["left", zoneR - visual.left],
    ["bottom", visual.bottom - zoneT],
    ["top", zoneB - visual.top],
  ];
  const out: Insets[] = [];
  for (const [side, px] of pushes) {
    if (px <= 0.5 || px > 2000) continue;
    out.push({ ...padding, [side]: padding[side] + px });
  }
  return out;
}

export interface ClearFitOptions extends FitOptions {
  /** Corner overlays the art must stay `clearance` px away from. */
  obstacles?: readonly ScreenRect[];
  /** How far the drawn art extends past the fit points. */
  chrome?: Insets;
  clearance?: number;
  /**
   * Largest screen-px shift of the padded center a corner nudge may introduce.
   * A one-sided pad recenters the quest in the gap and spends the opposite margin.
   */
  maxCenterShift?: number;
}

/**
 * {@link fitRotated}, then grow padding so the drawn box stays `clearance` px off
 * corner obstacles. Picks the nudge that keeps the most zoom. One camera move uses the result.
 */
export function fitRotatedClear(points: readonly LngLat[], bearing: number, opts: ClearFitOptions): RotatedFit | null {
  let padding = toInsets(opts.padding);
  const chrome = opts.chrome ?? { top: 0, right: 0, bottom: 0, left: 0 };
  const gap = opts.clearance ?? 0;
  const obstacles = opts.obstacles ?? [];
  const maxShift = opts.maxCenterShift ?? Infinity;
  let view = fitRotated(points, bearing, { ...opts, padding });
  if (!view || !obstacles.length) return view;
  for (let pass = 0; pass < 4; pass++) {
    const box = fittedScreenBox(points, bearing, view, opts.width, opts.height, padding, chrome);
    if (!box) return view;
    const hitting = obstacles.filter((obs) => hitsObstacle(box, obs, gap));
    if (!hitting.length) return view;
    let best: { padding: Insets; view: RotatedFit; overlap: number } | null = null;
    for (const obs of hitting) {
      for (const pad of pushPadding(padding, box, obs, gap)) {
        if (paddingCenterShift(padding, pad) > maxShift) continue;
        const next = fitRotated(points, bearing, { ...opts, padding: pad });
        if (!next) continue;
        const nextBox = fittedScreenBox(points, bearing, next, opts.width, opts.height, pad, chrome);
        if (!nextBox) continue;
        let overlap = 0;
        for (const o of obstacles) {
          if (!hitsObstacle(nextBox, o, gap)) continue;
          const zoneL = o.left - gap;
          const zoneT = o.top - gap;
          const zoneR = o.right + gap;
          const zoneB = o.bottom + gap;
          const w = Math.min(nextBox.right, zoneR) - Math.max(nextBox.left, zoneL);
          const h = Math.min(nextBox.bottom, zoneB) - Math.max(nextBox.top, zoneT);
          if (w > 0 && h > 0) overlap += w * h;
        }
        if (
          !best ||
          overlap < best.overlap - 0.5 ||
          (Math.abs(overlap - best.overlap) <= 0.5 && next.zoom > best.view.zoom)
        ) {
          best = { padding: pad, view: next, overlap };
        }
      }
    }
    if (!best || best.overlap >= overlapArea(box, hitting, gap) - 0.5) return view;
    padding = best.padding;
    view = best.view;
  }
  return view;
}

function overlapArea(visual: ScreenRect, obstacles: readonly ScreenRect[], gap: number): number {
  let area = 0;
  for (const o of obstacles) {
    if (!hitsObstacle(visual, o, gap)) continue;
    const w = Math.min(visual.right, o.right + gap) - Math.max(visual.left, o.left - gap);
    const h = Math.min(visual.bottom, o.bottom + gap) - Math.max(visual.top, o.top - gap);
    if (w > 0 && h > 0) area += w * h;
  }
  return area;
}
